import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { useLanguage } from "@/contexts/LanguageContext";
import { Badge } from "@/components/ui/badge";
import { useTeamEquipmentTotal } from "@/hooks/useTeamEquipmentTotal";
import { useTeamGuardsEquipmentTotal } from "@/hooks/useTeamGuardsEquipmentTotal";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";

export const GuardTeamEquipmentLists = () => {
  const { t } = useLanguage();

  // Guard Equipment
  const { data: guardEquipment, isLoading: guardLoading } = useQuery<any[]>({
    queryKey: ['guard-equipment-summary'],
    queryFn: async () => {
      // @ts-ignore - Table types not yet updated
      const { data, error } = await supabase
        .from('guard_equipment')
        .select(`
          equipment_type_id,
          quantity,
          equipment_types!inner (
            id,
            name,
            base_price
          )
        `)
        .eq('status', 'assigned');
      
      if (error) throw error;
      if (!data) return [];
      
      // Aggregate by equipment type
      const aggregated = data.reduce((acc: any[], item: any) => {
        const existing = acc.find(a => a.equipment_type_id === item.equipment_type_id);
        if (existing) {
          existing.quantity += item.quantity;
        } else {
          acc.push({
            equipment_type_id: item.equipment_type_id,
            name: item.equipment_types?.name || '',
            base_price: item.equipment_types?.base_price || 0,
            quantity: item.quantity,
          });
        }
        return acc;
      }, []);
      
      return aggregated;
    },
  });

  // Team Equipment
  const { data: teamEquipment, isLoading: teamLoading } = useQuery<any[]>({
    queryKey: ['team-equipment-summary'],
    queryFn: async () => {
      // @ts-ignore - Table types not yet updated
      const { data, error } = await supabase
        .from('team_equipment')
        .select(`
          equipment_type_id,
          quantity,
          team_equipment_types!inner (
            id,
            name,
            base_price
          )
        `)
        .eq('status', 'assigned');
      
      if (error) throw error;
      if (!data) return [];
      
      // Aggregate by equipment type
      const aggregated = data.reduce((acc: any[], item: any) => {
        const existing = acc.find(a => a.equipment_type_id === item.equipment_type_id);
        if (existing) {
          existing.quantity += item.quantity;
        } else {
          acc.push({
            equipment_type_id: item.equipment_type_id,
            name: item.team_equipment_types?.name || '',
            base_price: item.team_equipment_types?.base_price || 0,
            quantity: item.quantity,
          });
        }
        return acc;
      }, []);
      
      return aggregated;
    },
  });

  const calculateTotal = (items: any[]) => {
    return items?.reduce((sum, item) => sum + (item.quantity * item.base_price), 0) || 0;
  };

  return (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-semibold">{t('guardEquipmentList')}</h3>
        <p className="text-sm text-muted-foreground">{t('guardsEquipment')} & {t('teamsEquipment')}</p>
      </div>

      {/* Guards Equipment */}
      <Card>
        <CardHeader>
          <CardTitle>{t('guardsEquipment')}</CardTitle>
          <CardDescription>{t('guardEquipmentTypes')}</CardDescription>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>{t('name')}</TableHead>
                <TableHead>{t('supplier')}</TableHead>
                <TableHead>{t('quantity')}</TableHead>
                <TableHead>{t('unitPrice')}</TableHead>
                <TableHead className="text-right">{t('totalPrice')}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {guardLoading ? (
                <TableRow>
                  <TableCell colSpan={5} className="text-center">{t('loading')}</TableCell>
                </TableRow>
              ) : guardEquipment?.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={5} className="text-center">{t('noData')}</TableCell>
                </TableRow>
              ) : (
                <>
                  {guardEquipment?.map((item) => (
                    <TableRow key={item.equipment_type_id}>
                      <TableCell className="font-medium">{item.name}</TableCell>
                      <TableCell>-</TableCell>
                      <TableCell>{item.quantity}</TableCell>
                      <TableCell>€{item.base_price.toFixed(2)}</TableCell>
                      <TableCell className="text-right">€{(item.quantity * item.base_price).toFixed(2)}</TableCell>
                    </TableRow>
                  ))}
                  <TableRow className="font-bold bg-muted/50">
                    <TableCell colSpan={4}>{t('total')}</TableCell>
                    <TableCell className="text-right">€{calculateTotal(guardEquipment).toFixed(2)}</TableCell>
                  </TableRow>
                </>
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Teams Equipment */}
      <Card>
        <CardHeader>
          <CardTitle>{t('teamsEquipment')}</CardTitle>
          <CardDescription>{t('teamEquipmentTypes')}</CardDescription>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>{t('name')}</TableHead>
                <TableHead>{t('supplier')}</TableHead>
                <TableHead>{t('quantity')}</TableHead>
                <TableHead>{t('unitPrice')}</TableHead>
                <TableHead className="text-right">{t('totalPrice')}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {teamLoading ? (
                <TableRow>
                  <TableCell colSpan={5} className="text-center">{t('loading')}</TableCell>
                </TableRow>
              ) : teamEquipment?.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={5} className="text-center">{t('noData')}</TableCell>
                </TableRow>
              ) : (
                <>
                  {teamEquipment?.map((item) => (
                    <TableRow key={item.equipment_type_id}>
                      <TableCell className="font-medium">{item.name}</TableCell>
                      <TableCell>-</TableCell>
                      <TableCell>{item.quantity}</TableCell>
                      <TableCell>€{item.base_price.toFixed(2)}</TableCell>
                      <TableCell className="text-right">€{(item.quantity * item.base_price).toFixed(2)}</TableCell>
                    </TableRow>
                  ))}
                  <TableRow className="font-bold bg-muted/50">
                    <TableCell colSpan={4}>{t('total')}</TableCell>
                    <TableCell className="text-right">€{calculateTotal(teamEquipment).toFixed(2)}</TableCell>
                  </TableRow>
                </>
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
};